'use client'

/**
 * RadioCardGroup — a vertical radio list rendered as selectable cards.
 *
 * Extracted at #13458 from `@/components/settings/personalization-axis-radio`,
 * which had the layout, the in-flight disable and the fieldset/legend
 * semantics welded to a hard-coded "Auto" option meaning "no override".
 * `/settings/profile`'s three-way `timezoneMode` control needs the first
 * three and has no use for the fourth, so the sentinel stayed at the caller:
 * `PersonalizationAxisRadio` maps its own `null` ↔ `'__auto__'` and hands
 * this component plain strings, which is all an `<input type="radio">` can
 * carry anyway.
 *
 * ## Backed by `@sai/ui`'s `RadioGroup` (#14974)
 *
 * The radios are the package's `RadioGroup`/`RadioGroupRadio`, which is
 * `@radix-ui/react-radio-group`: one tab stop for the whole group (it lands
 * on the checked radio), arrow keys that move AND select with wrap-around,
 * and Space to select. That is the WAI-ARIA radio-group pattern, and it is
 * the same keyboard model native `<input type="radio">`s sharing a `name`
 * gave the previous body, so no caller sees a behaviour change. Each radio is
 * a `<button role="radio">`; inside a `<form>` Radix adds a hidden native
 * input per option, so form submission still carries `name=value`.
 *
 * The `<fieldset>`/`<legend>` wrapper stays. It is what gives the group its
 * `role="group"` name, which suites across the app address — changing a
 * shared component's role is a locator sweep this issue does not own. The
 * inner `radiogroup` is named by the same legend (and described by the
 * description) via `aria-labelledby`/`aria-describedby`, so a screen reader
 * entering the radios directly still hears what they choose between.
 *
 * ## `htmlFor`/`id` AND a wrapping label
 *
 * Both, deliberately. The wrapper is what makes the whole card a click
 * target; the explicit pair is what keeps the association if a future option
 * gains a second focusable child inside the card, which silently ends the
 * implicit association. A `<button>` is a labelable element, so the pair
 * works for the package radio exactly as it did for an `<input>`.
 *
 * Pure input: reports selection via `value` / `onChange`, never persists.
 * `onChange` is wrapped in `useTransition` internally so the radios disable
 * while a caller's server action is in flight.
 */

import { RadioGroup, RadioGroupRadio } from '@sai/ui/components'
import { useId, useTransition, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface RadioCardOption {
  /** Submitted/reported value. Must be unique within the group. */
  value: string
  /** Primary line — what the option is. */
  label: ReactNode
  /** Supporting line — what choosing it does. */
  description?: ReactNode
  /** Muted, right-aligned trailing content (an offset, a count, a badge). */
  trailing?: ReactNode
}

export interface RadioCardGroupProps {
  /** Visible `<legend>`, and the group's accessible name. */
  legend: ReactNode
  /** Optional paragraph below the legend. Omitted entirely when absent. */
  description?: ReactNode
  /** Currently selected value. */
  value: string
  options: readonly RadioCardOption[]
  /**
   * Called with the newly selected value. Wrapped in `useTransition`
   * internally, so the radios disable for the duration of an async caller.
   */
  onChange: (next: string) => void | PromiseLike<void>
  /**
   * Shared `name` for the inputs. Defaults to a generated id, which is what
   * makes two groups on one page independent without the caller thinking
   * about it.
   */
  name?: string
  disabled?: boolean
  className?: string
  testId?: string
}

export function RadioCardGroup({
  legend,
  description,
  value,
  options,
  onChange,
  name,
  disabled = false,
  className,
  testId,
}: RadioCardGroupProps) {
  const generatedName = useId()
  const idPrefix = useId()
  const [isPending, startTransition] = useTransition()

  const groupName = name ?? generatedName
  const isDisabled = disabled || isPending

  const handleChange = (next: string) => {
    // A synchronous caller (local `useState`) runs as an ordinary urgent
    // update. Routing it through an async transition too defers the
    // caller's own state behind the package radio's click, so the selection
    // and anything rendered from it lag the input (#14974).
    const result = onChange(next)
    // Keyed on `.then`, not `instanceof Promise`: a promise from another
    // realm, or any other thenable, must still hold the in-flight disable.
    if (typeof (result as PromiseLike<void> | undefined)?.then !== 'function') {
      return
    }
    // An async caller's promise is AWAITED inside an ASYNC transition scope.
    // A synchronous scope that fires `void onChange(next)` returns
    // immediately, so React ends the transition in microseconds and
    // `isPending` never covers the round trip — the documented in-flight
    // disable would clear before the server action had even been dispatched,
    // and every assertion of it passes trivially (#13458 review I3).
    startTransition(async () => {
      await result
    })
  }

  const legendId = `${idPrefix}-legend`
  const descriptionId = `${idPrefix}-description`

  return (
    <fieldset className={cn('space-y-3', className)} data-testid={testId}>
      <legend id={legendId} className="text-sm font-medium text-text-primary">
        {legend}
      </legend>
      {description && (
        <p
          id={descriptionId}
          className="text-sm text-text-secondary"
          data-testid="radio-card-group-description"
        >
          {description}
        </p>
      )}
      <RadioGroup
        ariaLabelledBy={legendId}
        aria-describedby={description ? descriptionId : undefined}
        name={groupName}
        value={value}
        onValueChange={handleChange}
        disabled={isDisabled}
        className="gap-2"
      >
        {options.map(option => {
          const optionId = `${idPrefix}-${option.value}`
          const isSelected = value === option.value
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border border-stroke-regular bg-surface-elevated p-3 transition-colors',
                isSelected && 'border-action-primary bg-surface-panel',
                isDisabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <RadioGroupRadio
                id={optionId}
                value={option.value}
                size="sm"
                className="mt-0.5"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-text-primary">
                  {option.label}
                </span>
                {option.description && (
                  <span className="block text-xs text-text-secondary">
                    {option.description}
                  </span>
                )}
              </span>
              {option.trailing && (
                <span className="flex-shrink-0 text-xs text-text-secondary">
                  {option.trailing}
                </span>
              )}
            </label>
          )
        })}
      </RadioGroup>
    </fieldset>
  )
}
